/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Modal } from 'react-bootstrap'
import './ActiveCallStyle.css'
import Keypad from '../Keypad/Keypad.component'
import { getFullName } from '@utils/fullname.util'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { InputField, Button } from '@components/common'
import {
  formatPhoneNumber,
  normalizePhoneNumber,
} from '@utils/numberFormatter.util'
import {
  createNewTwilioDevice,
  makeOutgoingCall,
  makeOutgoingExtensionCall,
} from '@utils/device.twilio'
import { IContact } from '@dataTypes/contact.type'
import {
  createConversation,
  fetchAllConversations,
  updateCallsById,
  updateSelectedMessage,
  setCallerInfo,
} from '@store/actions'
import { fetchCallRecordingSettingsApi } from '@services/api/call-recording-settings.api'
import { endCallConference, setCallOnHold } from '@services/api/calls.api'
import {
  startConferenceRecording,
  stopConferenceRecording,
} from '@store/actions/calls.action'
import { useNavigate } from 'react-router-dom'
import { ChatChannel } from '@dataTypes/chat.type'
import { NoteContextType } from '@dataTypes/note.type'
import { setCurrentCall, setHasActiveCall } from '@store/reducers/calls.reducer'
import { useActiveCall } from '../../../contexts/ActiveCallContext'
import {
  SET_ACTIVE_TAB,
  SET_INPUT_NUMBER,
  SET_SHOW_MODAL,
} from '@store/reducers/messageCenter.reducer'
import Avatar from '@components/common/Avatar/Avatar.component'
import { useNoteEditorOverlay } from '@components/common/NoteEditorOverlay'
import type { Patient } from '@dataTypes/PatientInfo.types'
import { ICallDirection } from '@dataTypes/calls.type'

import { logger } from '@utils/safeLogger'

type ActiveCallProps = {
  show: boolean
  onHide?: () => void
  isMuted?: boolean
  isModal?: boolean
}

const inputNumberegex = /^[0-9*+#()\-]*$/

const normalizeDialDigits = (raw: string): string => raw.replace(/\D/g, '')

function ActiveCall({ ...props }: ActiveCallProps): React.ReactElement {
  const { openNoteEditor } = useNoteEditorOverlay()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const callerInfo = useAppSelector((state) => state.calls.callerInfo)
  const clinic = useAppSelector((state) => state.clinic.selectedClinic)
  const conversation = useAppSelector((state) => state.chat.data)
  const device = useAppSelector((state) => state.calls.device)
  const currentCall = useAppSelector((state) => state.calls.currentCall)
  const {
    callStatus,
    setCallStatus,
    showNumpad,
    setShowNumpad,
    callTopText,
    setCallTopText,
    callMuted,
    setCallMuted,
    callPaused,
    setCallPaused,
    startCallTimer,
    stopCallTimer,
    pauseCallTimer,
    callDisplayTime,
    conferenceSid,
  } = useActiveCall()
  const contacts = useAppSelector((state) => state.contacts.data)
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const internalExtensions = useAppSelector(
    (state) => state.settingsCommunication.internalExtensions,
  )
  const phoneDevices = useAppSelector(
    (state) => state.settingsCommunication.phoneDevices,
  )
  const inputNumber = useAppSelector((state) => state.messageCenter.inputNumber)
  const [displayName, setDisplayName] = useState<string | null>(
    getFullName(callerInfo?.contact || null).length
      ? getFullName(callerInfo?.contact || null)
      : null,
  )
  const [bottomText, setBottomText] = useState<string>('')
  const [matchedContact, setMatchedContact] = useState<IContact | null>(null)
  const [autoStartRecording, setAutoStartRecording] = useState(true)
  const [manualRecordingActive, setManualRecordingActive] = useState(false)
  const [recordingBusy, setRecordingBusy] = useState(false)

  const callerInfoRef = useRef(callerInfo)
  const currentCallRef = useRef(currentCall)
  const conversationRef = useRef(conversation)

  useEffect(() => {
    const clinicId = selectedClinic?.clinic_id
    if (!props.show || !clinicId) return
    void fetchCallRecordingSettingsApi(clinicId)
      .then((res) => {
        if (res.statusCode === 200 && res.data?.call_recording_settings) {
          setAutoStartRecording(
            res.data.call_recording_settings.auto_start !== false,
          )
        }
      })
      .catch(() => {
        setAutoStartRecording(true)
      })
  }, [props.show, selectedClinic?.clinic_id])

  const onDelete = () => {
    dispatch(SET_INPUT_NUMBER(inputNumber.slice(0, -1)))
  }

  const onKeyClick = (key: string) => {
    if (inputNumberegex.test(key)) {
      dispatch(SET_INPUT_NUMBER(inputNumber + key))
    }
  }

  const getContactDetails = (number: string): IContact | null => {
    return (
      contacts.find((c) =>
        [
          normalizePhoneNumber(c.cellphone),
          ...(c.home_phone ? [normalizePhoneNumber(c.home_phone)] : []),
          ...(c.work_phone ? [normalizePhoneNumber(c.work_phone)] : []),
        ].includes(normalizePhoneNumber(number)),
      ) ?? null
    )
  }

  const handleStartCall = async () => {
    setCallStatus('Ringing')

    if (!device) return

    const dialDigits = normalizeDialDigits(inputNumber)
    const deviceExtMatch = phoneDevices.find(
      (d) =>
        d.device_extension_number &&
        normalizeDialDigits(d.device_extension_number) === dialDigits,
    )
    const extMatch = deviceExtMatch
      ? undefined
      : internalExtensions.find(
          (e) => normalizeDialDigits(e.extension_number) === dialDigits,
        )
    const isExtensionDial =
      dialDigits.length > 0 && !!(deviceExtMatch || extMatch)

    const displayReceiver =
      deviceExtMatch?.device_name ??
      extMatch?.name ??
      formatPhoneNumber(inputNumber, false)

    if (isExtensionDial && selectedClinic) {
      if (deviceExtMatch) setDisplayName(deviceExtMatch.device_name)
      else if (extMatch) setDisplayName(extMatch.name)

      await makeOutgoingExtensionCall(
        device,
        dispatch,
        selectedClinic,
        displayReceiver,
        dialDigits,
      )
    } else {
      await makeOutgoingCall(
        device,
        { cellphone: normalizePhoneNumber(inputNumber) },
        dispatch,
        selectedClinic ?? undefined,
      )
    }

    setTimeout(() => {
      if (callerInfoRef.current && callerInfoRef.current.call_id) {
        if (callerInfoRef.current.call_status === 'Ringing') {
          console.info('No answer - Disconnecting call')
          currentCallRef.current?.disconnect()
          device?.calls?.forEach((call) => call.disconnect())
          dispatch(
            updateCallsById({
              call_id: callerInfoRef.current.call_id,
              updates: {
                call_status: 'No Answer',
              },
            }),
          )
          dispatch(setHasActiveCall(false))
          dispatch(setCurrentCall(null))
          dispatch(setCallerInfo(null))
        }
      }
    }, 25000)
  }

  const handleAnswerCall = () => {
    currentCall?.accept()

    dispatch(setHasActiveCall(true))
    dispatch(setCallerInfo({ ...callerInfo, call_status: 'In Progress' }))
    setCallStatus('In Progress')
  }

  const handleEndCall = async () => {
    console.info('Ending call')
    stopCallTimer()
    setCallStatus('Completed')

    device?.calls?.forEach((call) => call.disconnect())
    currentCall?.reject()
    currentCall?.disconnect()

    if (callerInfo?.call_sid) {
      try {
        const sid = selectedClinic?.twilio_account_sid?.trim()
        const token = selectedClinic?.twilio_auth_token?.trim()
        if (sid && token) {
          await endCallConference(sid, token, callerInfo.call_sid)
        }
      } catch (error) {
        logger.error('Failed to end call conference:', error)
      }
    }

    // Clear call state
    dispatch(setHasActiveCall(false))
    dispatch(setCurrentCall(null))
    dispatch(setCallerInfo(null))
  }

  const handleDeclineCall = () => {
    setCallStatus('Busy')
    handleEndCall()
  }

  const handleDeclineAndSend = () => {
    const number = callerInfo?.caller_number
    if (number) {
      dispatch(SET_ACTIVE_TAB('chat'))
      navigate('/message')
      const chat = conversation?.find(
        (c) =>
          c.chat_participant.length === 1 &&
          c.chat_participant.find((p) => p?.phone_number === number),
      )
      if (chat && chat.chat_conversation_id) {
        dispatch(updateSelectedMessage({ id: chat.chat_conversation_id }))
      } else {
        const contact = getContactDetails(number)
        dispatch(
          createConversation({
            conversation_data: {
              channel: ChatChannel.sms,
              chat_message: [],
              chat_participant: [],
            },
            members_data: [
              {
                chat_conversation_id: '',
                phone_number: number,
                contact_id: contact?.contact_id ?? undefined,
              },
            ],
          }),
        )
      }
      setCallStatus('Busy')
      handleDeclineCall()
    }
  }

  const onMicClick = () => {
    const nextMuted = !callMuted
    currentCall?.mute(nextMuted)
    setCallMuted(nextMuted)
    setCallTopText(
      nextMuted ? 'On mute' : callTopText === 'On mute' ? '' : callTopText,
    )
  }

  const onRecordClick = async (): Promise<void> => {
    const conferenceSidToUse = conferenceSid ?? callerInfo?.call_sid
    const sid = selectedClinic?.twilio_account_sid?.trim()
    const token = selectedClinic?.twilio_auth_token?.trim()
    const clinicId = selectedClinic?.clinic_id
    const dbName = localStorage.getItem('db_name') ?? ''
    if (!conferenceSidToUse || !sid || !token || !clinicId || !dbName) return
    const direction =
      callerInfo?.call_type === ICallDirection.Outgoing
        ? ICallDirection.Outgoing
        : ICallDirection.Incoming
    setRecordingBusy(true)
    try {
      if (manualRecordingActive) {
        await dispatch(
          stopConferenceRecording({
            sid,
            token,
            conferenceSid: conferenceSidToUse,
          }),
        ).unwrap()
        setManualRecordingActive(false)
      } else {
        await dispatch(
          startConferenceRecording({
            sid,
            token,
            conferenceSid: conferenceSidToUse,
            db_name: dbName,
            clinic_id: clinicId,
            direction,
          }),
        ).unwrap()
        setManualRecordingActive(true)
      }
    } catch (error) {
      logger.error('Conference recording control failed:', error)
    } finally {
      setRecordingBusy(false)
    }
  }

  const onPauseClick = async () => {
    const nextPaused = !callPaused
    setCallPaused(nextPaused)

    const conferenceSidToUse = conferenceSid ?? callerInfo?.call_sid
    const dbName = localStorage.getItem('db_name') ?? undefined
    const clinicId = selectedClinic?.clinic_id
    if (nextPaused) {
      setCallTopText('On hold')
      currentCall?.mute(true)
      pauseCallTimer()

      if (
        callerInfo &&
        conferenceSidToUse &&
        selectedClinic?.twilio_account_sid &&
        selectedClinic?.twilio_auth_token
      ) {
        try {
          await setCallOnHold(
            selectedClinic.twilio_account_sid.trim(),
            selectedClinic.twilio_auth_token.trim(),
            conferenceSidToUse,
            true,
            dbName,
            clinicId,
          )
        } catch (error) {
          logger.error('Failed to set call on hold:', error)
        }
      }
    } else {
      setCallTopText('')
      currentCall?.mute(false)
      startCallTimer(true)
      if (
        callerInfo &&
        conferenceSidToUse &&
        selectedClinic?.twilio_account_sid &&
        selectedClinic?.twilio_auth_token
      ) {
        try {
          await setCallOnHold(
            selectedClinic.twilio_account_sid.trim(),
            selectedClinic.twilio_auth_token.trim(),
            conferenceSidToUse,
            false,
            dbName,
            clinicId,
          )
        } catch (error) {
          logger.error('Failed to unset call hold:', error)
        }
      }
    }
  }

  const onNotesClick = () => {
    console.info('Notes click')
    console.info('Associated caller: ', callerInfo)
    if (callerInfo) {
      openNoteEditor({
        patient:
          (callerInfo.contact?.patient as unknown as Patient | undefined) ??
          null,
        contactId: callerInfo.contact_id ?? null,
        contextId: callerInfo.call_id,
        contextType: NoteContextType.CALL,
        contextName: 'Call',
        openFirstNote: false,
        initialView: 'add',
        overlayBackLabel: 'Back to call',
        editorBackButtonLabel: 'Back to call',
        openContextNotesByDefault: true,
        notesFetchLoadingContainer: 'body',
        portalTarget: '.page-content',
      })
    }
    if (callTopText === 'Taking notes') {
      setCallTopText('')
    } else {
      setCallTopText('Taking notes')
    }
  }

  const getStatusClassName = useCallback((_status: string) => {
    return `status ${_status.trim().toLowerCase().split(' ').join('-')}`
  }, [])

  const onHide = useCallback((): void => {
    console.info('Hiding active call - clear state')
    dispatch(SET_SHOW_MODAL({ activeCall: false }))
    dispatch(SET_INPUT_NUMBER(''))
    if (props.onHide) props.onHide()
    stopCallTimer()
    device?.calls?.forEach((call) => call.disconnect())
    currentCall?.reject()
    currentCall?.disconnect()
    dispatch(setHasActiveCall(false))
    dispatch(setCurrentCall(null))
    dispatch(setCallerInfo(null))
  }, [dispatch, props.onHide, stopCallTimer, device, currentCall])

  const onInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const _input = e.target.value
    if (inputNumberegex.test(_input)) {
      dispatch(SET_INPUT_NUMBER(_input))
    }
  }

  useEffect(() => {
    setMatchedContact(getContactDetails(inputNumber))
  }, [inputNumber])

  useEffect(() => {
    if (props.show && callerInfo) {
      startCallTimer()
    }
  }, [props.show, callerInfo, startCallTimer])

  useEffect(() => {
    if (!device && selectedClinic?.clinic_id) {
      createNewTwilioDevice(dispatch)
    }
  }, [device, selectedClinic?.clinic_id, dispatch])

  useEffect(() => {
    switch (callStatus) {
      case 'Ringing':
        setBottomText('Calling...')
        break
      case 'In Progress':
        {
          setShowNumpad(false)
          startCallTimer()
        }
        break
      case 'Busy':
        {
          setBottomText('Busy')
          setShowNumpad(false)
          setTimeout(() => {
            setBottomText('')
            dispatch(setCallerInfo(null))
            console.info('Busy - setting caller info to null')
            device?.calls?.forEach((call) => call.disconnect())
            onHide()
          }, 1000)
        }
        break
      case 'Completed':
        {
          console.info('Completed - stopping call timer')
          stopCallTimer()
          setBottomText('Call ended')
          setTimeout(() => {
            setBottomText('')
            dispatch(setHasActiveCall(false))
            dispatch(setCurrentCall(null))
            dispatch(setCallerInfo(null))
            device?.calls?.forEach((call) => call.disconnect())
            onHide()
          }, 1000)
        }
        break
      case 'On Hold':
        break
      default:
        setBottomText('')
        break
    }
  }, [callStatus, startCallTimer, dispatch, device, onHide, stopCallTimer])

  useEffect(() => {
    const callInfoStatus = callerInfo?.call_status || ''
    const callInfoNumber =
      (callerInfo?.call_type === 'Incoming'
        ? callerInfo?.caller_number
        : callerInfo?.receiver_number) ?? ''
    const _contactDetails = getContactDetails(callInfoNumber)
    if (_contactDetails) {
      const fullName = getFullName(_contactDetails)
      setDisplayName(fullName.length ? fullName : null)
    }

    if (['Failed', 'No Answer'].includes(callInfoStatus)) {
      setCallStatus('Busy')
      dispatch(setCallerInfo(null))
    } else if (callInfoStatus.length) {
      setCallStatus(callInfoStatus)
    }

    if (callInfoNumber.length) dispatch(SET_INPUT_NUMBER(callInfoNumber))

    if (callerInfo?.call_type === 'Incoming' && callInfoStatus === 'Ringing')
      setShowNumpad(false)
    callerInfoRef.current = callerInfo
  }, [callerInfo, dispatch])

  useEffect(() => {
    callerInfoRef.current = callerInfo
  }, [callerInfo])

  useEffect(() => {
    currentCallRef.current = currentCall
  }, [currentCall])

  useEffect(() => {
    conversationRef.current = conversation
  }, [conversation])

  useEffect(() => {
    if (clinic) {
      dispatch(fetchAllConversations())
    }
  }, [clinic])

  return (
    <div className="active-call-container">
      {callerInfo?.call_type === 'Incoming' && callStatus === 'Ringing' ? (
        <>
          {props.isModal && (
            <div className="acc-header">
              <div className="acc-header-text">
                <span className="ti ti-grip-vertical acc-drag-button" />
                <Modal.Title>Incoming call</Modal.Title>
              </div>
              <button
                type="button"
                className="unstyled-button acc-header-close mgc_close_fill"
                aria-label="Close"
                onClick={onHide}
              />
            </div>
          )}
          <div className="icm-call-profile">
            <div className="icm-image-wrapper">
              {callerInfo?.contact?.photo ? (
                <Avatar
                  className="icm-caller-img"
                  source={callerInfo?.contact?.photo}
                  alt="Contact image"
                />
              ) : (
                <span className="mgc_user_3_fill no-profile-image" />
              )}
            </div>
            <div className="icm-caller-info">
              <span className="icm-caller-name">
                {displayName ?? formatPhoneNumber(inputNumber, false)}
              </span>
              <span className="small-text">is calling...</span>
            </div>
            <div className="incoming-call-buttons">
              <Button className="button-primary" onClick={handleAnswerCall}>
                Answer
              </Button>
              <Button
                className="button-destructive-secondary decline-btn"
                onClick={handleDeclineCall}
              >
                Decline
              </Button>
            </div>
            <div className="incoming-call-buttons">
              <Button
                className="decline-send-btn button-secondary"
                onClick={handleDeclineAndSend}
              >
                Decline and Send a Message
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          {currentCall || callStatus !== 'Dialing' ? (
            <>
              <div className="acc-header-text">
                {props.isModal && (
                  <span className="ti ti-grip-vertical acc-drag-button" />
                )}
                <div className="main-display">
                  {callTopText !== '' && (
                    <div className={getStatusClassName(callTopText)}>
                      {callTopText}
                    </div>
                  )}
                  <div className="d-flex w-100 justify-content-between align-items-center">
                    <span className="acm-caller-name">
                      {displayName ?? formatPhoneNumber(inputNumber, false)}
                    </span>
                    <span className="small-text">
                      {callStatus === 'In Progress'
                        ? callDisplayTime
                        : bottomText}
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {props.isModal && (
                <div className="acc-header">
                  <div className="acc-header-text">
                    <span className="ti ti-grip-vertical acc-drag-button" />
                    <Modal.Title>Outgoing call</Modal.Title>
                  </div>
                  <button
                    type="button"
                    className="unstyled-button acc-header-close mgc_close_fill"
                    aria-label="Close"
                    onClick={onHide}
                  />
                </div>
              )}
              <InputField
                className={`acm-input-field${matchedContact ? ' acm-input-with-contact' : ''}`}
                value={inputNumber}
                placeholder={'(000) 0000-000'}
                onChange={onInputChange}
                leftIcon={
                  matchedContact ? (
                    <>
                      <span className="acm-contact-name">
                        {getFullName(matchedContact)}
                      </span>
                      <span className="acm-contact-number">
                        {formatPhoneNumber(inputNumber, false)}
                      </span>
                    </>
                  ) : undefined
                }
                rightIcon={
                  inputNumber.length ? (
                    <span className="mgc_delete_back_fill" />
                  ) : undefined
                }
                onRightIconClick={onDelete}
                type="string"
              />
            </>
          )}
          {showNumpad && (
            <Keypad
              onClick={onKeyClick}
              disabled={['Ringing', 'Busy'].includes(callStatus)}
            />
          )}
          {['Dialing', 'Ringing', 'Busy'].includes(callStatus) ? (
            <Button
              className={`dial-end-btn ${callStatus === 'Dialing' ? 'mgc_phone_fill dial-btn' : 'mgc_phone_off_fill end-btn'}`}
              disabled={inputNumber.length === 0 || callStatus === 'Busy'}
              onClick={
                callStatus === 'Dialing' ? handleStartCall : handleEndCall
              }
            />
          ) : (
            <div className="action-buttons">
              <Button
                className={`mgc_mic_fill${callMuted ? '' : ' active'}`}
                onClick={onMicClick}
                disabled={callStatus === 'Completed'}
              />
              <Button
                className={`mgc_pause_fill${callPaused ? ' active' : ''}`}
                onClick={onPauseClick}
                disabled={callStatus === 'Completed'}
              />
              {!autoStartRecording && (
                <Button
                  className={`mgc_radiobox_fill${manualRecordingActive ? ' active' : ''}`}
                  onClick={() => {
                    void onRecordClick()
                  }}
                  disabled={callStatus === 'Completed' || recordingBusy}
                  title={
                    manualRecordingActive ? 'Stop recording' : 'Start recording'
                  }
                />
              )}
              <Button
                className={`active mgc_dot_grid_line${showNumpad ? ' active' : ''}`}
                onClick={() => setShowNumpad(!showNumpad)}
              />
              <Button
                className="mgc_document_3_fill"
                onClick={onNotesClick}
                disabled={callStatus === 'Completed'}
              />
              <Button
                className="end-btn mgc_phone_off_fill"
                disabled={callStatus === 'Completed'}
                onClick={handleEndCall}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ActiveCall
